/**
 * web/src/lib/posthog.ts
 * Runtime initialisation of posthog-js, the cosmetic half of the hybrid
 * feature-flag model (docs/FEATURE-FLAGS.md).
 *
 * Two non-obvious rules this enforces:
 *  - RUNTIME key, not build-time. We init from `me.posthog` (delivered by
 *    /api/me) — NOT a `VITE_POSTHOG_KEY`, because Vite inlines VITE_* at build
 *    time and a staging build promoted to prod would carry the staging key.
 *  - Server-seeded bootstrap. We pass `me.flags` as posthog-js's `bootstrap`
 *    feature flags + `distinctID = me.id`, so the client's initial flag state
 *    matches the server's evaluation exactly (no load flicker, no divergence).
 *
 * When PostHog is unconfigured (`me.posthog === null` — the default for local
 * dev, the demo, and any env without a project key), this is a complete no-op:
 * posthog-js never initialises and `useFlag` still works off `me.flags`.
 */
import { useEffect } from 'react';
import posthog from 'posthog-js';
import type { Me } from '../api/types';

// Module-level guard: StrictMode double-invokes effects in dev, and `me` can
// change reference; we only ever call init() once per page load.
let initialized = false;

/**
 * Initialise posthog-js (once) and identify the user, from the server-delivered
 * runtime config. Safe to call with `me === undefined` (no-ops until it
 * resolves) so it can sit above the auth early-returns in RequireAuth.
 *
 * @param me the authenticated user, or undefined while /api/me is pending
 * @sideEffects initialises the posthog-js singleton + calls identify (only when
 *   `me.posthog` is non-null, i.e. PostHog is configured for this env)
 * @idempotent init runs at most once; identify is safe to repeat
 */
export function usePostHog(me: Me | undefined): void {
  useEffect(() => {
    const config = me?.posthog;
    if (!me || !config) return;
    if (!initialized) {
      posthog.init(config.key, {
        api_host: config.host,
        // Seed initial flags from the server so client + server agree on render 0.
        bootstrap: { distinctID: me.id, featureFlags: me.flags },
        capture_pageview: true,
        // Off: don't capture arbitrary clicks/inputs on an internal tool (PII).
        autocapture: false,
      });
      initialized = true;
    }
    posthog.identify(me.id, { email: me.email, role: me.role });
  }, [me]);
}
